// ─── Pure helpers (outside component — stable, never recreated) ───────────────

import {
  IEnrolledEmployeeDocsEntity,
  IOvertimeDocsEntity,
  ITimeSheetDocsEntity,
} from "../../typings"
import {
  GroupedDepartment,
  GroupedOvertimeDepartment,
  TimesheetGroupBy,
} from "../component/TimesheetContent"
import { formatGroupLabel } from "pages/employee-dashboard/time-and-attendance/utils"

export const toggleIds = (current: number[], ids: number[]): number[] => {
  const allSelected = ids.every((id) => current.includes(id))
  return allSelected
    ? current.filter((id) => !ids.includes(id))
    : [...current, ...ids.filter((id) => !current.includes(id))]
}

export const buildFlags = (e: ITimeSheetDocsEntity): string[] => [
  ...(e.geofenceStatus ? [String(e.geofenceStatus)] : []),
  ...(e.lateClockin ? ["Late Clock-in"] : []),
  ...(e.lateClockout ? ["Late Clock-out"] : []),
  ...(e.origin ? [String(e.origin)] : []),
  ...(e.overtime ? ["Overtime"] : []),
]

export const groupByDepartmentNoOvertime = (
  docs: ITimeSheetDocsEntity[],
  shouldGroup: boolean
): GroupedDepartment[] => {
  if (!shouldGroup)
    return [{ department: { id: 0, name: "All" }, employees: docs }]

  const map = docs.reduce((acc, data) => {
    // Rows without a department go under "Unknown Department" (id 0)
    // rather than being dropped, so the CSV export keeps every row.
    const deptId = data?.employee?.department?.id ?? 0
    if (!acc[deptId]) {
      acc[deptId] = {
        department: {
          id: deptId,
          name: data?.employee?.department?.description || "Unknown Department",
        },
        employees: [],
      }
    }
    acc[deptId].employees.push(data)
    return acc
  }, {} as Record<number, GroupedDepartment>)

  return Object.values(map)
}

export const toDateGroups = (
  docsByDate: Record<string, ITimeSheetDocsEntity[]>,
  groupBy: TimesheetGroupBy,
  newestFirst: boolean
): GroupedDepartment[] =>
  Object.entries(docsByDate)
    .filter(([, rows]) => rows.length)
    .sort(([a], [b]) => (newestFirst ? b.localeCompare(a) : a.localeCompare(b)))
    .map(([key, rows]) => ({
      department: { id: key, name: formatGroupLabel(key, groupBy) },
      employees: rows,
    }))

export const groupByDepartmentWithOvertime = (
  docs: IOvertimeDocsEntity[],
  shouldGroup: boolean
): GroupedOvertimeDepartment[] => {
  if (!docs?.length) return []
  if (!shouldGroup) {
    return [
      {
        department: { id: 0, name: "All" },
        employees: docs.flatMap((d) => d.records ?? []),
      },
    ]
  }
  return docs.map((dept) => ({
    department: { id: dept.departmentId, name: dept.departmentName },
    employees: dept.records ?? [],
  }))
}

export const formatYMD = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export const startOfWeek = (date: Date): Date => {
  const d = new Date(date)
  const day = d.getDay()
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1))
  return d
}

export const addDays = (date: Date, days: number): Date => {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

export const formatDate = (date: Date, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-US", options).format(date)

export const normalizeDate = (date: string) => date.split("T")[0]

export const toKeyDate = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}

export const formatTime = (time: string | undefined | null): string => {
  if (!time) return "—"
  const [hour, minute] = time.split(":").map(Number)
  if (isNaN(hour) || isNaN(minute)) return "—"
  const date = new Date()
  date.setHours(hour, minute)
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const today = new Date()

export const todaysDate = today.toLocaleDateString("en-US", {
  month: "short",
  day: "numeric",
})

export const currentMonth = today.toLocaleDateString("en-US", { month: "long" })

export const TIMESHEET_HEADERS = [
  { label: "Name", width: "240px" },
  { label: "Day", width: "100px" },
  { label: "Shift Plan", width: "120px" },
  { label: "Shift Period", width: "120px" },
  { label: "Clock-In", width: "120px" },
  { label: "Clock-Out", width: "120px" },
  { label: "Method", width: "160px" },
  { label: "Flags", width: "152px" },
  { label: "Reason", width: "248px" },
  { label: "Action", width: "120px" },
]

export const OVERTIME_HEADERS = [
  { label: "Name", width: "200px" },
  { label: "Overtime start", width: "164px" },
  { label: "Overtime end", width: "164px" },
  { label: "Method", width: "164px" },
  { label: "Reason", width: "280px" },
  { label: "Action", width: "148px" },
]
export const formatRate = (numerator: number, denominator: number): string => {
  if (!denominator) return "0%"
  return `${((numerator / denominator) * 100).toFixed(0)}%`
}

export const formatTimeAdherence = (
  totalAssigned: number,
  earlyClockOuts: number
): string => {
  if (!totalAssigned) return "0%"
  return `${(((totalAssigned - earlyClockOuts) / totalAssigned) * 100).toFixed(
    0
  )}%`
}

export const groupByDepartment = (
  docs: IEnrolledEmployeeDocsEntity[]
): Record<
  string,
  {
    departmentId: number
    departmentName: string
    employees: IEnrolledEmployeeDocsEntity[]
  }
> =>
  docs.reduce(
    (acc, employee) => {
      const deptId = employee.department?.id ?? 0
      const deptName = employee.department?.name || "Unknown Department"
      const key = String(deptId)
      if (!acc[key]) {
        acc[key] = {
          departmentId: deptId,
          departmentName: deptName,
          employees: [],
        }
      }
      acc[key].employees.push(employee)
      return acc
    },
    {} as Record<
      string,
      {
        departmentId: number
        departmentName: string
        employees: IEnrolledEmployeeDocsEntity[]
      }
    >
  )

export const getInitials = (name = "") =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
